import {Disclosure, Switch} from '@headlessui/react';
import {InformationCircleIcon} from '@heroicons/react/24/outline';
import * as d3 from 'd3';
import React from 'react';
import {Tooltip} from 'react-tooltip';
import {useQueryParam} from 'use-query-params';

import {useStagingForecastToggle} from 'src/contexts/StagingToggleContext';
import {useSelectedForecastTimes} from 'src/hooks/useSelectedForecastTimes';
import {ApiUserProfile} from 'src/lib/api/auth';
import {SeasonalSeriesType, SeriesType, Site} from 'src/lib/api/project';
import {
  AllColumns,
  DashboardSection,
  FLOW_OBSERVATION_SOURCES,
  FlowForecastColumns,
  FlowHistoricalDailyColumns,
  FlowObservationColumns,
  ForecastTime,
  ForecastTimeSeriesQuery,
  ForecastTimesBySource,
  RateVolumeMode,
  SeasonalModelVisualMode,
  Source,
  TimeAggregation,
  WeatherColumns,
  WeatherHistoricalDailyColumns,
  isCumulativeMode,
} from 'src/lib/api/series';
import COLORS, {getRFCColors} from 'src/lib/colors';
import {
  addDays,
  addSeconds,
  addUTCDays,
  makeDateRangeQueryParam,
  makeGetSelectionForIntervalShortcuts,
  now,
} from 'src/lib/dates';
import featureFlags from 'src/lib/featureFlags';
import {
  getDefaultRateVolumeMode,
  getSeasonalHistoryDays,
  getSeasonalHorizon,
  isInvalidLevelMode,
  makeValidRateVolumeMode,
  seasonalDashboardSection,
} from 'src/lib/siteUtils';
import * as tw from 'src/lib/tailwindClasses';

import {recordEvent} from './Analytics';
import ForecastFlowTextSummary, {SmartSummaryButton} from './ForecastFlowTextSummary';
import ForecastSelect from './ForecastSelect';
import {FreezingLine} from './FreezingLine';
import TimeSeriesProvider from './TimeSeriesProvider';
import TimeSeriesViz, {
  SOLO_VIZ_HEIGHT,
  SOLO_VIZ_HEIGHT_WITH_LEGEND,
  STACKED_VIZ_HEIGHT,
  STACKED_VIZ_HEIGHT_WITH_LEGEND,
} from './TimeSeriesViz';
import {Translator, useTranslations} from './TranslationsContext';
import Viz from './Viz';
import {
  VizConfig,
  VizConfigCreatorItem,
  filterConfigToAvailableTimeSeries,
  hfGetStyle,
  makeConfigItems,
  makeLevelPercentileConfigs,
  makeRequestFromVizConfig,
} from './Viz/config';
import WithWidth from './WithWidth';

/**
 * This file is a fork of Seasonal-2. It can diverge as we make future changes to the visualization of
 * the new Seasonal-3 models; It lets us keep Seasonal-2 as is for current customers without worrying about staying
 * backwards compatible.
 */

type TimeAggregationKey = 'daily' | '10 day' | '30 day' | '90 day' | 'monthly' | 'quarterly';

const TIME_AGGREGATIONS: {[key in TimeAggregationKey]: TimeAggregation} = {
  daily: '1D',
  '10 day': '10D',
  '30 day': '30D',
  '90 day': '90D',
  monthly: 'MS',
  quarterly: 'QS',
};

const DEFAULT_TIME_AGGREGATION_KEY = 'daily';

type AggState = {
  timeAggregation: TimeAggregation;
  rateVolumeMode: RateVolumeMode;
};

function AggOptions({
  site,
  agg,
  updateAgg,
  showLevelOption = true,
}: {
  site: Site;
  showTracesDropdown: boolean;
  agg: AggState;
  updateAgg: (update: Partial<AggState>) => void;
  showLevelOption?: boolean;
}) {
  const {t} = useTranslations();
  return (
    <div className="space-x-2">
      <select
        className={tw.select}
        value={agg.rateVolumeMode}
        onChange={(event) => {
          const newMode = event.target.value as RateVolumeMode;
          updateAgg({rateVolumeMode: newMode});
          recordEvent('aggregation_option_changed', {
            section: 'seasonal',
            option: 'rate_volume_mode',
            value: newMode,
          });
        }}
      >
        <option value="rate">{t('rate')}</option>
        <option value="volume">{t('volume')}</option>
        <option value="cumulative-volume">{t('cumulative-volume')}</option>
        <option value="cumulative-volume-from-now">{t('cumulative-volume-from-now')}</option>
        {showLevelOption && site.seriesTypes.includes(SeriesType.Level) && (
          <option value="level">{t('level')}</option>
        )}
      </select>
      <select
        className={tw.select}
        value={agg.timeAggregation}
        onChange={(event) => {
          const timeAgg = event.target.value as TimeAggregation;
          updateAgg({timeAggregation: timeAgg});
          recordEvent('aggregation_option_changed', {
            section: 'seasonal',
            option: 'time_aggregation',
            value: timeAgg,
          });
        }}
      >
        {Object.keys(TIME_AGGREGATIONS).map((aggKey) => {
          const timeAgg = TIME_AGGREGATIONS[aggKey as TimeAggregationKey];
          return (
            <option key={aggKey} value={timeAgg}>
              {t(aggKey as TimeAggregationKey)}
            </option>
          );
        })}
      </select>
    </div>
  );
}

export default function Seasonal3({
  site,
  seriesType: seasonalFlowSeriesType,
  defaultDateRange: defaultDateRangeProp,
  currentUser,
  isStaging,
  setIsStaging,
  seasonalModelVisualMode,
}: {
  site: Site;
  seriesType: SeasonalSeriesType;
  defaultDateRange?: [Date, Date];
  currentUser: ApiUserProfile | null;
  isStaging: boolean;
  setIsStaging: (isStaging: boolean) => void;
  seasonalModelVisualMode: SeasonalModelVisualMode;
}) {
  const {t} = useTranslations();
  const {shouldShowStagingForecastToggles} = useStagingForecastToggle();
  const [showSummary, setShowSummary] = React.useState(false);
  const horizonDays = getSeasonalHorizon(site);
  const historyDays = getSeasonalHistoryDays(site);

  const section = seasonalDashboardSection(seasonalFlowSeriesType);
  const additionalSourceTraces: ForecastTimeSeriesQuery[] =
    site.forecastTimeSeries[seasonalFlowSeriesType].filter(
      (q) =>
        q.source === Source.HydroForecastSeasonal3GEFS ||
        q.source === Source.HydroForecastSeasonal3ERA5
    ) ?? [];
  const showTracesDropdown = additionalSourceTraces.length > 0;
  const seasonalSourceName: Source = (() => {
    // if Seasonal-3 is the release model, make API queries for `hydroforecast-seasonal`.
    // If we're in customer preview mode, make API queries for `hydroforecast-seasonal-3`.
    // If we're in internal preview mode, make API queries for `hydroforecast-seasonal-3-internal-preview`.
    if (
      seasonalModelVisualMode ===
        SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3CustomerPreview ||
      seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal3CustomerPreviewWithNoReleaseModel
    ) {
      return Source.HydroForecastSeasonal3;
    } else if (
      seasonalModelVisualMode ===
        SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3InternalPreview ||
      seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal3InternalPreviewWithNoReleaseModel
    ) {
      return Source.HydroForecastSeasonal3InternalPreview;
    } else {
      return Source.HydroForecastSeasonal;
    }
  })();

  const defaultDateRange = React.useMemo(
    () =>
      defaultDateRangeProp ||
      ([
        now(site.localTimeZone).minus({days: historyDays}).startOf('day').toJSDate(),
        now(site.localTimeZone).plus({days: horizonDays}).endOf('day').toJSDate(),
      ] as [Date, Date]),
    [site.localTimeZone, defaultDateRangeProp, horizonDays, historyDays]
  );

  const DateRangeQueryParam = React.useMemo(
    () => makeDateRangeQueryParam(defaultDateRange),
    [defaultDateRange]
  );

  const [dateRange, setDateRange] = useQueryParam('seasonalDateRange', DateRangeQueryParam);

  const [hfAgg, updateHfAgg] = React.useReducer(
    (state: AggState, action: Partial<AggState>) => {
      return {...state, ...action};
    },
    {
      timeAggregation: TIME_AGGREGATIONS[DEFAULT_TIME_AGGREGATION_KEY],
      rateVolumeMode: getDefaultRateVolumeMode(site),
    }
  );

  React.useEffect(() => {
    updateHfAgg({rateVolumeMode: getDefaultRateVolumeMode(site)});
  }, [site]);

  const [weatherAgg, updateWeatherAgg] = React.useReducer(
    (state: AggState, action: Partial<AggState>) => {
      return {...state, ...action};
    },
    {
      timeAggregation: TIME_AGGREGATIONS[DEFAULT_TIME_AGGREGATION_KEY],
      rateVolumeMode: 'rate',
    }
  );

  React.useEffect(() => {
    if (isInvalidLevelMode(weatherAgg.rateVolumeMode, site)) {
      updateWeatherAgg({rateVolumeMode: 'rate'});
    }
  }, [weatherAgg.rateVolumeMode, site, updateWeatherAgg]);

  // hfAgg and weatherAgg are eventually validated in the useEffect,
  // but we need to ensure they are valid on initial render to avoid noisy api errors
  const hfRateVolumeMode = makeValidRateVolumeMode(hfAgg.rateVolumeMode, site);
  const weatherRateVolumeMode = makeValidRateVolumeMode(weatherAgg.rateVolumeMode, site);

  const hfConfig = filterConfigToAvailableTimeSeries(
    makeHFConfig(
      site,
      hfAgg.timeAggregation,
      hfRateVolumeMode,
      additionalSourceTraces,
      seasonalSourceName,
      seasonalFlowSeriesType,
      t
    ),
    site
  );
  const weatherConfig = filterConfigToAvailableTimeSeries(
    makeWeatherConfig(weatherAgg.timeAggregation, weatherRateVolumeMode, t),
    site
  );

  const forecastSources = [...hfConfig.forecasts, ...weatherConfig.forecasts].map((c) => c.source);

  const adjustedDateRange = [dateRange[0], addSeconds(dateRange[1], 1)] as [Date, Date];

  const {
    primaryForecastTimes,
    selectedPrimaryForecastTimes,
    selectedForecastTimesBySource,
    setSelectedPrimaryForecastTimes,
    onNextPrimaryForecastTimesChange,
  } = useSelectedForecastTimes(
    site,
    dateRange,
    horizonDays,
    seasonalSourceName,
    forecastSources,
    true,
    isStaging
  );

  // We conditionally show the contents of this component, but never unmount the Seasonal3 component itself,
  // even if we switch to showing Seasonal-2 via the staging toggle. This means we keep the state
  // of the dropdowns etc. so aggstate doesn't get lost when you switch between staging and nonstaging.
  const shouldShowSeasonal3Plot: boolean =
    seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal3Release ||
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3CustomerPreview ||
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3InternalPreview ||
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal3InternalPreviewWithNoReleaseModel ||
    seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal3CustomerPreviewWithNoReleaseModel;

  if (!shouldShowSeasonal3Plot) {
    return <></>;
  }

  const forecastSelections = (
    <>
      <div className="flex items-center space-x-4">
        {/* Show the staging toggle only if it's allowed by the feature flag & this is the release model (aka not in preview mode) */}
        {featureFlags.STAGING_TOGGLE(currentUser) &&
          shouldShowStagingForecastToggles &&
          seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal3Release && (
            <>
              <Switch
                id="staging-toggle"
                checked={isStaging}
                onChange={() => setIsStaging(!isStaging)}
                className={`${isStaging ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full`}
              >
                <span
                  className={`${isStaging ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
              <label htmlFor="staging-toggle" className="text-sm text-gray-600">
                {t('stagingForecasts')}
              </label>
            </>
          )}
        <ForecastSelect
          horizon={horizonDays}
          site={site}
          dateRange={dateRange}
          defaultDateRange={defaultDateRange}
          setDateRange={setDateRange}
          primaryForecastTimes={primaryForecastTimes}
          selectedPrimaryForecastTimes={selectedPrimaryForecastTimes}
          setSelectedPrimaryForecastTimes={setSelectedPrimaryForecastTimes}
          onNextPrimaryForecastTimesChange={onNextPrimaryForecastTimesChange}
          shortcuts={{
            absolute: [
              {
                displayName: t('latest'),
                getSelection: (forecastTimes: ForecastTime[]) => [forecastTimes[0]],
              },
              {
                displayName: t('latestYTD'),
                getSelection: (forecastTimes: ForecastTime[]) => {
                  const latest = forecastTimes[0];
                  return [latest];
                },
                startDate: new Date(new Date().getFullYear(), 0, 1),
                endDate: addDays(new Date(), horizonDays),
              },
              {
                displayName: t('latestAndLastWeek'),
                getSelection: (forecastTimes: ForecastTime[]) => {
                  const latest = forecastTimes[0];
                  const lastWeekInitializationTime = addUTCDays(latest.initializationTime, -7);
                  const lastWeek = forecastTimes.find(
                    (t) => t.initializationTime.getTime() === lastWeekInitializationTime.getTime()
                  )!;

                  return [latest, lastWeek];
                },
              },
            ],
            relative: [
              {
                displayName: t('every10Days'),
                getSelection: makeGetSelectionForIntervalShortcuts(
                  selectedPrimaryForecastTimes,
                  [10, 20, 30],
                  addDays(dateRange[1], -10)
                ),
                startDate: dateRange[0],
                endDate: dateRange[1],
              },
              {
                displayName: t('every30Days'),
                getSelection: makeGetSelectionForIntervalShortcuts(
                  selectedPrimaryForecastTimes,
                  [30, 60, 90],
                  addDays(dateRange[1], -30)
                ),
                startDate: dateRange[0],
                endDate: dateRange[1],
              },
            ],
          }}
        />
      </div>
    </>
  );

  // Seasonal-3 plot with "preview mode" styling
  if (
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3CustomerPreview ||
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3InternalPreview ||
    seasonalModelVisualMode ===
      SeasonalModelVisualMode.Seasonal3CustomerPreviewWithNoReleaseModel ||
    seasonalModelVisualMode === SeasonalModelVisualMode.Seasonal3InternalPreviewWithNoReleaseModel
  ) {
    return (
      <div className={tw.section}>
        <Disclosure defaultOpen={false}>
          {({open}) => (
            <>
              <div className={tw.sectionHeader}>
                <div className="flex items-start justify-between w-full min-h-[3rem] py-2">
                  <Disclosure.Button
                    as="h2"
                    className={tw.sectionTitle + ' cursor-pointer flex items-center md:min-w-80'}
                  >
                    <span>
                      {seasonalModelVisualMode ===
                        SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3CustomerPreview ||
                      seasonalModelVisualMode ===
                        SeasonalModelVisualMode.Seasonal3CustomerPreviewWithNoReleaseModel ? (
                        t('seasonal3CustomerPreview')
                      ) : (
                        <>
                          <div>{t('seasonal3InternalPreview')}</div>
                          <div className="italic text-sm text-gray-600 font-normal">
                            {t('internallyVisibleOnly')}
                          </div>
                        </>
                      )}
                    </span>
                    {open ? (
                      <>
                        <InformationCircleIcon
                          className="w-5 h-5 text-gray-500 ml-2"
                          data-tooltip-id="seasonal-3-preview-tooltip"
                        />

                        <Tooltip
                          id="seasonal-3-preview-tooltip"
                          className="z-[9999] max-w-md !bg-gray-50 !rounded-md !text-black !text-sm !font-normal"
                          place="bottom"
                          opacity={1}
                          border="1px solid lightgray"
                        >
                          <p>
                            {seasonalModelVisualMode ===
                              SeasonalModelVisualMode.Seasonal3CustomerPreviewWithNoReleaseModel ||
                            seasonalModelVisualMode ===
                              SeasonalModelVisualMode.Seasonal3InternalPreviewWithNoReleaseModel
                              ? t('previewWithoutUpgradeDescription')
                              : t('previewDescription')}
                          </p>
                        </Tooltip>
                      </>
                    ) : (
                      <div className="w-1 h-4"></div> // empty div for spacing
                    )}
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform mr-2 ml-2 ${
                        open ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </Disclosure.Button>
                  {open ? (
                    forecastSelections
                  ) : (
                    <div className="flex items-center space-x-4">
                      <p className="text-sm text-gray-700">
                        {seasonalModelVisualMode ===
                          SeasonalModelVisualMode.Seasonal3CustomerPreviewWithNoReleaseModel ||
                        seasonalModelVisualMode ===
                          SeasonalModelVisualMode.Seasonal3InternalPreviewWithNoReleaseModel
                          ? t('previewWithoutUpgradeDescription')
                          : t('previewDescription')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <Disclosure.Panel>
                <WithWidth>
                  {(width) => (
                    <div className={tw.sectionBody}>
                      <TimeSeriesProvider
                        site={site}
                        section={section}
                        forecastTimesBySource={selectedForecastTimesBySource}
                        dateRange={adjustedDateRange}
                        isStaging={isStaging}
                        {...makeRequestFromVizConfig(hfConfig)}
                      >
                        {(vizProps) => {
                          return (
                            <Viz
                              section={t(DashboardSection.Seasonal)}
                              title={hfRateVolumeMode === 'level' ? t('waterLevel') : t('flow')}
                              renderOptions={() => (
                                <AggOptions
                                  site={site}
                                  agg={{...hfAgg, rateVolumeMode: hfRateVolumeMode}}
                                  showTracesDropdown={showTracesDropdown}
                                  updateAgg={updateHfAgg}
                                />
                              )}
                              site={site}
                              loadingStateDims={[width, SOLO_VIZ_HEIGHT_WITH_LEGEND]}
                              dateRange={dateRange}
                              setDateRange={setDateRange}
                              config={hfConfig}
                              {...vizProps}
                            >
                              {(props) => (
                                <TimeSeriesViz
                                  {...props}
                                  timeZone={site.localTimeZone}
                                  dims={[width, SOLO_VIZ_HEIGHT]}
                                />
                              )}
                            </Viz>
                          );
                        }}
                      </TimeSeriesProvider>
                      {/* Note that there are no weather charts in the preview */}
                    </div>
                  )}
                </WithWidth>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    );
  }

  // Seasonal-3 plot with full release styling
  return (
    <div className={tw.section}>
      <div className={tw.sectionHeader}>
        <div className="flex items-center gap-4">
          <h2 className={tw.sectionTitle}>{t(DashboardSection.Seasonal)}</h2>
          {!showSummary && (
            <SmartSummaryButton siteId={site.id} onClick={() => setShowSummary(true)} />
          )}
        </div>
        {forecastSelections}
      </div>
      {showSummary && (
        <div className="my-4 p-4 bg-blue-50 rounded-lg">
          <ForecastFlowTextSummary
            site={site}
            source={Source.HydroForecastSeasonal}
            horizonDays={horizonDays}
            section={section}
          />
        </div>
      )}
      <WithWidth>
        {(width) => (
          <div className={tw.sectionBody}>
            <TimeSeriesProvider
              site={site}
              section={section}
              forecastTimesBySource={selectedForecastTimesBySource}
              dateRange={adjustedDateRange}
              isStaging={isStaging}
              {...makeRequestFromVizConfig(hfConfig)}
            >
              {(vizProps) => {
                return (
                  <Viz
                    section={t(DashboardSection.Seasonal)}
                    title={hfRateVolumeMode === 'level' ? t('waterLevel') : t('flow')}
                    renderOptions={() => (
                      <AggOptions
                        site={site}
                        agg={{...hfAgg, rateVolumeMode: hfRateVolumeMode}}
                        showTracesDropdown={showTracesDropdown}
                        updateAgg={updateHfAgg}
                      />
                    )}
                    site={site}
                    loadingStateDims={[width, SOLO_VIZ_HEIGHT_WITH_LEGEND]}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    config={hfConfig}
                    {...vizProps}
                  >
                    {(props) => (
                      <TimeSeriesViz
                        {...props}
                        timeZone={site.localTimeZone}
                        dims={[width, SOLO_VIZ_HEIGHT]}
                      />
                    )}
                  </Viz>
                );
              }}
            </TimeSeriesProvider>
            <TimeSeriesProvider
              site={site}
              section={section}
              forecastTimesBySource={Object.keys(selectedForecastTimesBySource).reduce(
                (acc, source) => {
                  // this is a terrible hack to subtract a day (24 hours) from the GEFS issue
                  // time because any forecast younger than 28 hours (100800 seconds) will not
                  // have the full horizon.
                  if (source === Source.GEFS) {
                    const adjustmentThreshold = addSeconds(new Date(), -100800);
                    return {
                      ...acc,
                      [source]: selectedForecastTimesBySource[source]?.map((ft) => {
                        if (ft.initializationTime > adjustmentThreshold) {
                          return {
                            initializationTime: addDays(ft.initializationTime, -1),
                            issueTime: addDays(ft.issueTime, -1),
                          };
                        }
                        return ft;
                      }),
                    };
                  }
                  return acc;
                },
                {} as ForecastTimesBySource
              )}
              dateRange={adjustedDateRange}
              {...makeRequestFromVizConfig(weatherConfig)}
            >
              {(vizProps) => {
                return (
                  <Viz
                    section={t(DashboardSection.Seasonal)}
                    title={t('weather')}
                    renderOptions={() => (
                      <AggOptions
                        site={site}
                        agg={{...weatherAgg, rateVolumeMode: weatherRateVolumeMode}}
                        showTracesDropdown={false}
                        updateAgg={updateWeatherAgg}
                        showLevelOption={false}
                      />
                    )}
                    site={site}
                    loadingStateDims={[width, STACKED_VIZ_HEIGHT_WITH_LEGEND * 2]}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    config={weatherConfig}
                    {...vizProps}
                  >
                    {(props) => {
                      return (
                        <>
                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            range={[0, 'auto']}
                            minMaxRangeConstraints={[null, 0.1]}
                            groupId={t('precipitation')}
                            dims={[width, STACKED_VIZ_HEIGHT]}
                          />

                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            groupId={t('temperature')}
                            dims={[width, STACKED_VIZ_HEIGHT]}
                            renderSvg={({y, graphDims, observationTimeSeriesList}) => (
                              <FreezingLine
                                y={y}
                                graphDims={graphDims}
                                temperatureTimeSeries={
                                  observationTimeSeriesList.find(
                                    (ts) => !!ts.variables[WeatherColumns.TEMPERATURE]
                                  )!
                                }
                              />
                            )}
                          />
                        </>
                      );
                    }}
                  </Viz>
                );
              }}
            </TimeSeriesProvider>
          </div>
        )}
      </WithWidth>
    </div>
  );
}

const RFC_SOURCES_PRIORITIZED_ORDER = [
  {source: Source.NWRFC_ESP_NATURAL, displayName: 'NWRFC ESP Natural'},
  {source: Source.CNRFC_ESP_NATURAL, displayName: 'CNRFC ESP Natural'},
];
const SOURCE_TRACE_COLORS = [
  COLORS.SOURCE_TRACES_1,
  COLORS.SOURCE_TRACES_2,
  COLORS.SOURCE_TRACES_3,
];

// eslint-disable-next-line react-refresh/only-export-components
export function makeHFConfig(
  site: Site,
  timeAggregation: TimeAggregation,
  rateVolumeMode: RateVolumeMode,
  additionalHFForecastQueries: ForecastTimeSeriesQuery[],
  seasonalSourceName: Source,
  seasonalFlowSeriesType: SeriesType,
  t: Translator
): VizConfig {
  const forecastConfigCreators: VizConfigCreatorItem[] = [
    ...(rateVolumeMode === 'level'
      ? makeLevelPercentileConfigs({
          source: seasonalSourceName,
          curveFactory: d3.curveStepAfter,
          baseColor: COLORS.HF_50,
          displayNamePrefix: 'HydroForecast',
          legendGroup: 'HydroForecast',
        })
      : ([
          {
            displayName: 'HydroForecast 90% CI',
            source: seasonalSourceName,
            columns: [FlowForecastColumns.DISCHARGE_0_05, FlowForecastColumns.DISCHARGE_0_95],
            getStyle: () => ({
              fill: COLORS.HF_90,
              fillOpacity: 0.3,
            }),
            legendIcon: 'rect',
            curveFactory: d3.curveStepAfter,
            hideIfMultipleForecastTimes: true,
            legendOptions: {shortDisplayName: '90% CI', group: 'HydroForecast'},
          },
          {
            displayName: 'HydroForecast 50% CI',
            source: seasonalSourceName,
            columns: [FlowForecastColumns.DISCHARGE_0_25, FlowForecastColumns.DISCHARGE_0_75],
            getStyle: () => ({
              fill: COLORS.HF_50,
              fillOpacity: 0.6,
            }),
            legendIcon: 'rect',
            curveFactory: d3.curveStepAfter,
            hideIfMultipleForecastTimes: true,
            legendOptions: {shortDisplayName: '50% CI', group: 'HydroForecast'},
          },
        ] as VizConfigCreatorItem[])),
    {
      displayName: `HydroForecast ${t('mean')}`,
      source: seasonalSourceName,
      columns: [FlowForecastColumns.DISCHARGE_MEAN],
      getStyle: hfGetStyle,
      legendIcon: 'line',
      curveFactory: d3.curveStepAfter,
      showForecastTimeMarkers: true,
      legendOptions: {
        shortDisplayName: t('mean'),
        group: 'HydroForecast',
      },
    },
    {
      displayName: `HydroForecast ${t('median')}`,
      source: seasonalSourceName,
      columns: [FlowForecastColumns.DISCHARGE_MEDIAN],
      getStyle: hfGetStyle,
      legendIcon: 'line',
      defaultNotVisible: true,
      isHidden: true,
      curveFactory: d3.curveStepAfter,
      legendOptions: {
        shortDisplayName: t('median'),
        group: 'HydroForecast',
      },
    },
  ];

  const ADDITIONAL_HF_FORECASTS_NAMES: Partial<{
    [key in Source]: {name: string; traceName: string};
  }> = {
    [Source.HydroForecastSeasonal3ERA5]: {name: 'HF ERA5-Land Only', traceName: 'Analog traces'},
    [Source.HydroForecastSeasonal3GEFS]: {name: 'HF GEFS Only', traceName: 'Ensemble traces'},
  };
  forecastConfigCreators.push(
    ...additionalHFForecastQueries
      .map((query, index): VizConfigCreatorItem[] => {
        const sourceDisplayName = ADDITIONAL_HF_FORECASTS_NAMES[query.source as Source]?.name;
        const sourceTraceName = ADDITIONAL_HF_FORECASTS_NAMES[query.source as Source]?.traceName;
        const sourceColor = SOURCE_TRACE_COLORS[index % SOURCE_TRACE_COLORS.length];
        return [
          {
            displayName: `${sourceDisplayName} ${sourceTraceName}`,
            source: query.source,
            columns: query.columns!.filter((col) => /^\d+$/.test(col)), // filter to only numeric columns
            getStyle: () => ({
              stroke: sourceColor,
              strokeWidth: 0.6,
              strokeOpacity: 0.7,
            }),
            legendIcon: 'rect',
            curveFactory: d3.curveStepAfter,
            hideIfMultipleForecastTimes: true,
            hideTooltip: true,
            defaultNotVisible: true,
            legendOptions: {
              shortDisplayName: sourceTraceName,
              group: sourceDisplayName,
              onlyShowIfExpanded: true,
              ignoreGroupSelection: true,
            },
          },
          ...(rateVolumeMode === 'level'
            ? makeLevelPercentileConfigs({
                source: query.source,
                curveFactory: d3.curveStepAfter,
                baseColor: sourceColor,
                displayNamePrefix: sourceDisplayName!,
                legendGroup: sourceDisplayName!,
                onlyShowIfExpanded: true,
                defaultNotVisible: true,
              })
            : ([
                {
                  displayName: `${sourceDisplayName} 90% CI`,
                  source: query.source,
                  columns: [FlowForecastColumns.DISCHARGE_0_05, FlowForecastColumns.DISCHARGE_0_95],
                  getStyle: () => ({fill: sourceColor, fillOpacity: 0.3}),
                  legendIcon: 'rect',
                  curveFactory: d3.curveStepAfter,
                  hideIfMultipleForecastTimes: true,
                  defaultNotVisible: true,
                  legendOptions: {
                    shortDisplayName: '90% CI',
                    group: sourceDisplayName,
                    onlyShowIfExpanded: true,
                  },
                },
                {
                  displayName: `${sourceDisplayName} 50% CI`,
                  source: query.source,
                  columns: [FlowForecastColumns.DISCHARGE_0_25, FlowForecastColumns.DISCHARGE_0_75],
                  getStyle: () => ({fill: sourceColor, fillOpacity: 0.6}),
                  legendIcon: 'rect',
                  curveFactory: d3.curveStepAfter,
                  hideIfMultipleForecastTimes: true,
                  defaultNotVisible: true,
                  legendOptions: {
                    shortDisplayName: '50% CI',
                    group: sourceDisplayName,
                    onlyShowIfExpanded: true,
                  },
                },
              ] as VizConfigCreatorItem[])),
          {
            displayName: `${sourceDisplayName} ${t('mean')}`,
            source: query.source,
            columns: [FlowForecastColumns.DISCHARGE_MEAN],
            getStyle: () => ({stroke: sourceColor, strokeWidth: 2}),
            legendIcon: 'line',
            curveFactory: d3.curveStepAfter,
            defaultNotVisible: true,
            legendOptions: {
              shortDisplayName: t('mean'),
              group: sourceDisplayName,
              onlyShowIfExpanded: true,
            },
          },
        ];
      })
      .flat()
  );

  // Find which RFC source is available if any. Prioritize NWRFC if both exist.
  const rfcConfig = RFC_SOURCES_PRIORITIZED_ORDER.find((config) =>
    site.forecastTimeSeries[seasonalFlowSeriesType].some((q) => q.source === config.source)
  );

  if (rfcConfig) {
    forecastConfigCreators.push(
      ...([
        {
          displayName: `NOAA ${rfcConfig.displayName} 90% CI`,
          source: rfcConfig.source,
          columns: [FlowForecastColumns.DISCHARGE_0_05, FlowForecastColumns.DISCHARGE_0_95],
          getStyle: (idx = 0, numForecasts = 0) => {
            const rfcColors = getRFCColors(numForecasts);
            const strokeColor =
              numForecasts === 0 ? rfcColors[rfcColors.length - 1] : rfcColors[idx];
            return {
              fill: strokeColor,
              fillOpacity: 0.2,
            };
          },
          legendIcon: 'rect',
          curveFactory: d3.curveStepAfter,
          hideIfMultipleForecastTimes: true,
          defaultNotVisible: true,
          legendOptions: {
            shortDisplayName: '90% CI',
            group: 'NOAA',
            onlyShowIfExpanded: true,
          },
        },
        {
          displayName: `NOAA ${rfcConfig.displayName} 50% CI`,
          source: rfcConfig.source,
          columns: [FlowForecastColumns.DISCHARGE_0_25, FlowForecastColumns.DISCHARGE_0_75],
          getStyle: (idx = 0, numForecasts = 0) => {
            const rfcColors = getRFCColors(numForecasts);
            const strokeColor =
              numForecasts === 0 ? rfcColors[rfcColors.length - 1] : rfcColors[idx];
            return {
              fill: strokeColor,
              fillOpacity: 0.5,
            };
          },
          legendIcon: 'rect',
          curveFactory: d3.curveStepAfter,
          hideIfMultipleForecastTimes: true,
          defaultNotVisible: true,
          legendOptions: {
            shortDisplayName: '50% CI',
            group: 'NOAA',
            onlyShowIfExpanded: true,
          },
        },
        {
          displayName: `NOAA ${rfcConfig.displayName}`,
          source: rfcConfig.source,
          columns: [FlowForecastColumns.DISCHARGE_MEAN],
          getStyle: (idx = 0, numForecasts = 0) => {
            const rfcColors = getRFCColors(numForecasts);
            const strokeColor =
              numForecasts === 0 ? rfcColors[rfcColors.length - 1] : rfcColors[idx];
            return {stroke: strokeColor, strokeDasharray: 5};
          },
          legendIcon: 'line',
          showForecastTimeMarkers: true,
          curveFactory: d3.curveStepAfter,
          legendOptions: {
            shortDisplayName: rfcConfig.displayName,
            group: 'NOAA',
          },
        },
      ] as VizConfigCreatorItem[])
    );
  }

  // The deterministic NOAA RFC river forecast (Source.RFC) is distinct from the ESP-natural
  // sources above: it's a single trace whose only columns are the ones advertised in its config
  // (stage for the Mississippi barge sites), with no ensemble percentile columns -- so it renders
  // as one line rather than CI bands. We drive its columns off whatever the API advertises and
  // only show it in level mode, where stage plots directly on the height axis (in rate mode the
  // seasonal plot is in discharge space).
  const rfcDeterministicQuery = site.forecastTimeSeries[seasonalFlowSeriesType].find(
    (q) => q.source === Source.RFC
  );
  if (rfcDeterministicQuery?.columns?.length && rateVolumeMode === 'level') {
    forecastConfigCreators.push({
      displayName: 'NOAA RFC',
      source: Source.RFC,
      columns: rfcDeterministicQuery.columns,
      getStyle: (idx = 0, numForecasts = 0) => {
        const rfcColors = getRFCColors(numForecasts);
        const strokeColor = numForecasts === 0 ? rfcColors[rfcColors.length - 1] : rfcColors[idx];
        return {stroke: strokeColor, strokeDasharray: 5};
      },
      legendIcon: 'line',
      showForecastTimeMarkers: true,
      curveFactory: d3.curveStepAfter,
      legendOptions: {
        shortDisplayName: 'RFC',
        group: 'NOAA',
      },
    });
  }

  return {
    forecasts: makeConfigItems('forecasts', forecastConfigCreators, {
      timeAggregation,
      rateVolumeMode,
    }),
    observations: makeConfigItems(
      'observations',
      [
        {
          displayName: t('historicalMean'),
          source: Source.FLOW_HISTORICAL_DAILY,
          columns: [FlowHistoricalDailyColumns.FLOW_MEAN],
          getStyle: () => ({stroke: COLORS.OBS, strokeDasharray: 2}),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          isHidden: isCumulativeMode(rateVolumeMode),
          legendOptions: {
            shortDisplayName: t('historicalMean'),
            group: t('reference'),
          },
        },
        ...site.observationTimeSeries[seasonalFlowSeriesType]
          .filter(
            (q) => FLOW_OBSERVATION_SOURCES.includes(q.source) && q.columns && q.columns.length
          )
          .flatMap((q) => {
            // trend is based on the presence of the observed_clean column, which
            // is added by the api at request-time. If this is present, we will add
            // an additional timeseries, and style the Observed timeseries
            // differently, and toggle its visibility off by default
            const hasTrend = site.observationTimeSeries[seasonalFlowSeriesType].some(
              (q) =>
                FLOW_OBSERVATION_SOURCES.includes(q.source) &&
                q.columns &&
                q.columns.includes(FlowObservationColumns.observed_clean)
            );

            // because "Observed" can come in so many flavors, we use anything with a
            // valid Observation Source as observed, and trust the column provided by
            // the API completely
            const cs: VizConfigCreatorItem[] = [
              {
                displayName: t('observed'),
                source: q.source,
                columns: [q.columns![0]] as AllColumns[],
                getStyle: () => ({
                  hfStrokeOutlineStyle: {
                    stroke: 'white',
                    strokeWidth: 3,
                  },
                  stroke: COLORS.OBS,
                  strokeWidth: 2,
                  strokeOpacity: hasTrend ? 0.4 : 1,
                  fill: rateVolumeMode === 'level' ? COLORS.OBS : undefined,
                  fillOpacity: rateVolumeMode === 'level' ? 0.05 : undefined,
                }),
                legendIcon: 'line' as VizConfigCreatorItem['legendIcon'],
                curveFactory: d3.curveStepAfter,
                defaultNotVisible: hasTrend,
                isHidden: isCumulativeMode(rateVolumeMode),
                legendOptions: {
                  group: t('reference'),
                },
              },
            ];

            if (hasTrend) {
              cs.push({
                displayName: t('observedClean'),
                source: q.source,
                columns: [FlowObservationColumns.observed_clean],
                getStyle: () => ({
                  hfStrokeOutlineStyle: {
                    stroke: 'white',
                    strokeWidth: 3,
                  },
                  stroke: COLORS.OBS,
                  strokeWidth: 2,
                  strokeOpacity: 1,
                  fill: rateVolumeMode === 'level' ? COLORS.OBS : undefined,
                  fillOpacity: rateVolumeMode === 'level' ? 0.05 : undefined,
                }),
                legendIcon: 'line',
                curveFactory: d3.curveStepAfter,
                isHidden: isCumulativeMode(rateVolumeMode),
                legendOptions: {
                  group: t('reference'),
                },
              });
            }

            return cs;
          }),
      ],
      {timeAggregation, rateVolumeMode}
    ),
  };
}

function makeWeatherConfig(
  timeAggregation: TimeAggregation,
  rateVolumeMode: RateVolumeMode,
  t: Translator
): VizConfig {
  return {
    forecasts: makeConfigItems(
      'forecasts',
      [
        {
          displayName: `GEFS ${t('forecast')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.PRECIPITATION],
          getStyle: () => ({
            stroke: COLORS.WEATHER_3,
            fill: COLORS.WEATHER_3,
            fillOpacity: 0.1,
          }),
          legendIcon: 'rect',
          curveFactory: d3.curveStepAfter,
          showForecastTimeMarkers: true,
          groupId: t('precipitation'),
        },
        {
          displayName: `GEFS ${t('forecast')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.TEMPERATURE],
          getStyle: () => ({
            stroke: COLORS.WEATHER_3,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          showForecastTimeMarkers: true,
          groupId: t('temperature'),
        },
      ],
      {timeAggregation, rateVolumeMode}
    ),
    observations: makeConfigItems(
      'observations',
      [
        {
          displayName: `GEFS ${t('historicalMean')}`,
          source: Source.GEFS_HISTORICAL_DAILY,
          columns: [WeatherHistoricalDailyColumns.PRECIPITATION_MEAN],
          getStyle: () => ({
            hfStrokeOutlineStyle: {
              stroke: 'white',
              strokeWidth: 3,
            },
            stroke: COLORS.OBS,
            strokeDasharray: 2,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          groupId: t('precipitation'),
        },
        {
          displayName: `GEFS ${t('historicalMean')}`,
          source: Source.GEFS_HISTORICAL_DAILY,
          columns: [WeatherHistoricalDailyColumns.TEMPERATURE_MEAN],
          getStyle: () => ({
            stroke: COLORS.OBS,
            strokeDasharray: 2,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          groupId: t('temperature'),
        },
        {
          displayName: `GEFS ${t('observed')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.PRECIPITATION],
          getStyle: () => ({
            stroke: COLORS.OBS,
            fill: COLORS.OBS,
            fillOpacity: 0.1,
          }),
          legendIcon: 'rect',
          curveFactory: d3.curveStepAfter,
          groupId: t('precipitation'),
        },
        {
          displayName: `GEFS ${t('observed')}`,
          source: Source.GEFS,
          columns: [WeatherColumns.TEMPERATURE],
          getStyle: () => ({
            stroke: COLORS.OBS,
          }),
          legendIcon: 'line',
          curveFactory: d3.curveStepAfter,
          groupId: t('temperature'),
        },
      ],
      {timeAggregation, rateVolumeMode}
    ),
  };
}
